import type { OBB, Vec2 } from '../../../geo/types';
import type { Lot } from '../types';
import type { Plot } from '../plots';
import type { LotPlacer } from '../lotPlacer';
import { across } from '../acrossPlot';
import type { WoodcutGround } from './woodcutGround';

/* A plot too short to read is read on the ground a cell deep behind its house. */
function groundOf(plot: Plot, ground: WoodcutGround): OBB {
  return across(plot.lot, 0, Math.max(plot.depth, ground.grid.cell), 1);
}

/**
 * PLAN Lots, front rows by the woodcut (2026-10-05): the woodcut draws its streets wider than they
 * were and its rows that much further back, so a house is looked for from its planned place to
 * `reach` metres behind it, in `step`s. It stands down where `open` of its own ground is shown open
 * and nowhere in that reach does the woodcut draw a building on `built` of it: a garden, a court, a
 * yard or the lane inside the city wall along the street, not a row. A single such house in a
 * standing row is within what the registration is out by; `run` of them side by side are a gap.
 */
export const ROW = { reach: 8, step: 2, open: 0.5, built: 0.3, run: 2 } as const;
/* On the lane inside the city wall the woodcut draws gardens, trees and the odd house between the
   wall and the rows behind, and the wall registers it well: a house stands there only on its own
   drawn ground. The lanes are the sources named so (frontSources.ts). */
const LANE = /^lane-/;

function back(obb: OBB, facing: Vec2, by: number): OBB {
  return { ...obb, center: [obb.center[0] - facing[0] * by, obb.center[1] - facing[1] * by] };
}

function isDrawn(lot: Lot, ground: WoodcutGround): boolean {
  if (!ground.isRead(lot.obb) || ground.shares(lot.obb).open <= ROW.open) return true;
  const reach = LANE.test(lot.sourceId) ? 0 : ROW.reach;
  for (let by = 0; by <= reach; by += ROW.step)
    if (ground.shares(back(lot.obb, lot.facing, by)).built >= ROW.built) return true;
  return false;
}

/* The undrawn houses that stand in a run of at least `run` along their side of their street. */
function inOpenRuns(side: readonly Lot[], ground: WoodcutGround): readonly Lot[] {
  const ordered = [...side].sort((a, b) => a.ordinal - b.ordinal);
  const undrawn = ordered.map((lot) => !isDrawn(lot, ground));
  return ordered.filter((_, i) => {
    if (!undrawn[i]) return false;
    let from = i;
    let to = i;
    while (from > 0 && undrawn[from - 1]) from--;
    while (to < ordered.length - 1 && undrawn[to + 1]) to++;
    return to - from + 1 >= ROW.run;
  });
}

/** The front houses the woodcut draws; outside the walls, where it was not read, every house stands. */
export function housesDrawn(lots: readonly Lot[], ground: WoodcutGround): readonly Lot[] {
  const sides = new Map<string, Lot[]>();
  for (const lot of lots) {
    const key = `${lot.sourceId}:${lot.side}`;
    sides.set(key, [...(sides.get(key) ?? []), lot]);
  }
  const down = new Set([...sides.values()].flatMap((side) => inOpenRuns(side, ground)));
  return lots.filter((lot) => !down.has(lot));
}

/**
 * PLAN Lots, fill by the woodcut (2026-10-05): the plots behind the houses that are built on, those
 * the woodcut roofs; a bank plot's warehouse stands on the evidence of the 1544 plan instead. A plot
 * the woodcut was not read on is built on, as the ground outside its reading was before.
 */
export function plotsToFill(plots: readonly Plot[], ground: WoodcutGround): ReadonlySet<Plot> {
  return new Set(
    plots.filter((plot) => {
      if (plot.bank) return false;
      const behind = groundOf(plot, ground);
      return !ground.isRead(behind) || ground.showsBuilt(behind);
    })
  );
}

/** Of the built plots and the bank plots, those the woodcut roofs from end to end (`backlots.ts`). */
export function plotsToBuildThrough(
  plots: readonly Plot[],
  ground: WoodcutGround
): ReadonlySet<Plot> {
  return new Set(plots.filter((plot) => ground.roofedThrough(groundOf(plot, ground))));
}

/** A placer that also keeps the buildings behind the houses off the ground the woodcut shows open. */
export function offOpenGround(placer: LotPlacer, ground: WoodcutGround): LotPlacer {
  return { ...placer, fits: (obb) => placer.fits(obb) && !ground.showsOpen(obb) };
}
